import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Input } from "../ui/Input";
import { campersApi } from "../../api/campers";
import { useDebounce } from "../../hooks/useDebounce";
import api from "../../api/axios";
import type { User, Camper } from "../../types";

interface Props {
  user: User;
  /** Called after a successful link/unlink. */
  onChange?: (next: { formerCamperId: number | null; formerCamper: Camper | null }) => void;
}

export default function FormerCamperLink({ user, onChange }: Props) {
  const [linked, setLinked] = useState<Camper | null>(user.formerCamper ?? null);
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 300);
  const [results, setResults] = useState<Camper[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch up-to-date link if not provided in user prop
  useEffect(() => {
    if (user.formerCamper !== undefined) return;
    api
      .get<User & { formerCamper?: Camper | null }>(`/users/${user.id}`)
      .then((res) => setLinked(res.data.formerCamper ?? null))
      .catch(() => { /* non-fatal */ });
  }, [user.id, user.formerCamper]);

  useEffect(() => {
    if (linked) return;
    if (!debounced.trim()) { setResults([]); return; }
    setSearching(true);
    campersApi
      .list({ search: debounced, pageSize: 8 })
      .then((res) => setResults(res.data))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [debounced, linked]);

  const setLink = async (camperId: number | null) => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}/former-camper`, { camperId });
      if (camperId === null) {
        setLinked(null);
        onChange?.({ formerCamperId: null, formerCamper: null });
        toast.success("Link cleared");
      } else {
        const camper = results.find((c) => c.id === camperId) ?? null;
        setLinked(camper);
        setSearch("");
        setResults([]);
        onChange?.({ formerCamperId: camperId, formerCamper: camper });
        toast.success("Linked to former camper");
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || "Failed to update link");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-stone-700">
        Former camper record
      </label>
      {linked ? (
        <div className="flex items-center justify-between border border-stone-200 rounded-lg px-3 py-2">
          <div>
            <p className="text-sm font-medium text-stone-800">
              {linked.firstName} {linked.lastName}
            </p>
            {linked.dateOfBirth && (
              <p className="text-xs text-stone-500">{linked.dateOfBirth}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setLink(null)}
            disabled={saving}
            className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 font-medium disabled:opacity-50"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
            Unlink
          </button>
        </div>
      ) : (
        <>
          <Input
            placeholder="Search campers by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
          />
          {searching && <p className="text-xs text-stone-400">Searching…</p>}
          {results.length > 0 && (
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {results.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => setLink(c.id)}
                    className="w-full text-left border border-stone-200 hover:border-forest-300 hover:bg-forest-50 rounded-lg px-3 py-2 transition-colors disabled:opacity-50"
                  >
                    <p className="text-sm font-medium text-stone-800">
                      {c.firstName} {c.lastName}
                    </p>
                    <p className="text-xs text-stone-500">{c.dateOfBirth ?? "no DOB"}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!searching && debounced && results.length === 0 && (
            <p className="text-xs text-stone-400">No matches.</p>
          )}
        </>
      )}
    </div>
  );
}
